import {
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import React, { useEffect } from 'react';
import {
  PlusIcon,
  PencilSimpleIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react';
import ToolCreator from './ToolCreator';
import { useAlert } from '../../../../hooks/useAlert';
import DeleteModal from '../../../modals/DeleteModal';
import MessagePopup from '../../../common/MessagePopup';
import { deleteAgentToolApi, getAgentToolsApi } from '../../../../utils/apis';
import { UltravoxToolData } from '../../../../schemas/apis/interface';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../utils/redux';
import {
  setTools,
  setEditingTool,
  setToolCreatorOpen,
  removeTool,
} from '../../../../utils/redux/slices/toolsSlice';
import ToolDetails from './ToolDetails';

const UltravoxTools: React.FC = () => {
  const dispatch = useDispatch();
  const { tools, editingTool, isToolCreatorOpen } = useSelector(
    (state: RootState) => state.tools
  );

  // New state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [toolToDelete, setToolToDelete] =
    React.useState<UltravoxToolData | null>(null);

  // New state for tool details drawer
  const [isToolDetailsOpen, setIsToolDetailsOpen] = React.useState(false);
  const [selectedToolId, setSelectedToolId] = React.useState<string | null>(
    null
  );

  const { alert, setAlert, showAlert } = useAlert();

  const { data: toolsData, isLoading: isToolsLoading } = useQuery({
    queryKey: ['tools'],
    queryFn: getAgentToolsApi,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  const { mutate: deleteTool, isPending: isDeletingTool } = useMutation({
    mutationFn: deleteAgentToolApi,
    onSuccess: () => {
      showAlert('Ultravox tool deleted successfully', 'success');
      setIsDeleteModalOpen(false);
    },
    onError: () => {
      showAlert('Failed to delete ultravox tool', 'error');
    },
  });

  useEffect(() => {
    if (toolsData?.data?.data?.results) {
      dispatch(setTools(toolsData.data.data.results));
    }
  }, [toolsData, dispatch]);

  const handleEditTool = (tool: UltravoxToolData) => {
    dispatch(setEditingTool(tool));
    dispatch(setToolCreatorOpen(true));
  };

  const handleDeleteTool = (tool: UltravoxToolData) => {
    setToolToDelete(tool);
    setIsDeleteModalOpen(true);
  };

  // Handle actual deletion
  const handleConfirmDelete = () => {
    if (toolToDelete) {
      dispatch(removeTool(toolToDelete.toolId || ''));
      deleteTool(toolToDelete.toolId || '');
    }
  };

  const handleCloseToolCreator = () => {
    dispatch(setToolCreatorOpen(false));
    dispatch(setEditingTool(null));
  };

  const handleCreateNewTool = () => {
    dispatch(setEditingTool(null));
    dispatch(setToolCreatorOpen(true));
  };

  const renderToolList = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {tools.map((tool: UltravoxToolData, index: number) => (
          <Paper
            key={tool.toolId || index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
            onClick={() => {
              if (tool.toolId) {
                setSelectedToolId(tool.toolId);
                setIsToolDetailsOpen(true);
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Typography sx={{ fontWeight: 500 }}>{tool.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {tool.definition.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tool.ownership === 'public' ? (
                    <Chip
                      label="BuiltIn"
                      size="small"
                      sx={{
                        fontWeight: 400,
                        fontSize: '12px',
                        fontStyle: 'italic',
                        color: '#E74C3C',
                        backgroundColor: '#ffebee',
                      }}
                    />
                  ) : (
                    <Chip
                      label="Custom"
                      size="small"
                      sx={{
                        fontWeight: 400,
                        fontSize: '12px',
                        fontStyle: 'italic',
                        color: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      }}
                    />
                  )}
                </Typography>
              </Box>
              {tool.ownership === 'private' && (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleEditTool(tool);
                    }}
                    size="small"
                  >
                    <PencilSimpleIcon size={18} />
                  </IconButton>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteTool(tool);
                    }}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                  >
                    <TrashSimpleIcon size={18} weight="fill" color="#E74C3C" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <>
        <Typography
          sx={{ fontSize: '14px', fontWeight: '400', color: '#6B6F80' }}
        >
          No Ultravox Tools created yet. Create your first tool to get started
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlusIcon size={16} />}
          onClick={handleCreateNewTool}
          sx={{
            width: 'fit-content',
            height: '34px',
            borderRadius: '6px',
            textTransform: 'none',
            boxShadow: 'none',
            padding: '8px 16px',
          }}
        >
          Create Tool
        </Button>
      </>
    );
  };

  const renderToolListSkeleton = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {[...Array(3)].map((_, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="text" width={250} height={20} />
                <Skeleton variant="text" width={200} height={16} />
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="circular" width={30} height={30} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        height="100%"
        gap="16px"
        width="100%"
      >
        {isToolsLoading ? (
          renderToolListSkeleton()
        ) : tools.length > 0 ? (
          renderToolList()
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="100%"
            gap="16px"
            width="100%"
            py="16px"
            justifyContent="center"
          >
            {renderEmptyState()}
          </Box>
        )}
        <ToolCreator
          open={isToolCreatorOpen}
          onClose={handleCloseToolCreator}
          editingTool={editingTool}
        />

        {/* Tool Details Drawer */}
        <ToolDetails
          open={isToolDetailsOpen}
          onClose={() => {
            setIsToolDetailsOpen(false);
            setSelectedToolId(null);
          }}
          toolId={selectedToolId}
        />

        {/* Add the DeleteModal */}
        <DeleteModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleConfirmDelete}
          isPending={isDeletingTool}
          title="Delete Ultravox Tool"
          description="Are you sure you want to delete this ultravox tool? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
        />
      </Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default UltravoxTools;
